import React, { useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './hooks/useAuth';
import { AppDataProvider } from './context/AppDataContext';
import { router } from './routes';
import Landing, { LoadingScreen } from './pages/Landing';
import OnboardingFlow from './components/Onboarding/OnboardingFlow';
import ErrorBoundary from './components/ErrorBoundary';
import { gateUser, readSessionHint } from './utils/sessionCache';

/**
 * 인증 게이트 + 전역 데이터 공급만 담당한다.
 * 화면 구성은 routes/index.jsx 의 AppShell 이하로 위임한다.
 *
 * 공유받은 문장 화면(/share)으로 열렸고 지난번에 로그인해 있었으면, 로그인 확인(서버 왕복 두 번)을
 * 기다리지 않고 지난번 사본으로 바로 그린다 (utils/sessionCache). 확인이 끝나면 같은 자리에서
 * 진짜 사용자로 바뀐다 — 아래 트리 모양이 같아서 화면이 다시 만들어지지 않는다.
 */
const App = () => {
  const { user: confirmedUser, loading, error, signInWithGoogle, logout } = useAuth();
  const [launchPath] = useState(() => window.location.pathname);
  const [hint] = useState(readSessionHint);
  const user = gateUser({ user: confirmedUser, loading, hint, path: launchPath });

  if (loading && !user) {
    return (
      <>
        <LoadingScreen />
        <Toaster position="bottom-center" />
      </>
    );
  }

  if (!user) {
    return (
      <>
        <Landing signInWithGoogle={signInWithGoogle} authError={error} />
        <Toaster position="bottom-center" />
      </>
    );
  }

  if (user.isOnboardingCompleted !== true) {
    return (
      <div className="min-h-screen bg-white dark:bg-ink-950 text-ink-900 dark:text-ink-100">
        <OnboardingFlow onComplete={() => window.location.reload()} />
        <Toaster position="bottom-center" />
      </div>
    );
  }

  return (
    <ErrorBoundary message="앱을 불러오는 중 문제가 발생했습니다.">
      <AppDataProvider user={user} onLogout={logout}>
        <RouterProvider router={router} />
        <Toaster position="bottom-center" />
      </AppDataProvider>
    </ErrorBoundary>
  );
};

export default App;
